import React, { useEffect, useState } from "react";
import {
  Container,
  Box,
  Button,
  Typography,
  TextField,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Card,
  CardContent,
  Skeleton,
  OutlinedInput,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import { useRecoilState } from "recoil";
import {
  categoriesState,
  menuItemsState,
  orderIdState,
  selectedMenuItemsState,
} from "../atoms";
import { baseUrl } from "../constants";

function Category() {
  const { hotelId, shopId, tableId, tableNumber } = useParams();
  const navigate = useNavigate();
  const [categories, setCategories] = useRecoilState(categoriesState);
  const [menuItems, setMenuItems] = useRecoilState(menuItemsState);
  const [selectedMenuItems, setSelectedMenuItems] = useRecoilState(
    selectedMenuItemsState
  );
  const [orderId, setOrderId] = useRecoilState(orderIdState);

  const [selectedCategory, setSelectedCategory] = useState(null);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [loadingMenuItems, setLoadingMenuItems] = useState(true);
  const [searchTerm, setsearchTerm] = useState("");
  const [isDisableMenu, setIsDisableMenu] = useState(false);

  const productsWithValues = Object.values(selectedMenuItems).flatMap(
    (category) =>
      Object.entries(category)
        .filter(([productId, value]) => value > 0)
        .map(([productId, value]) => ({ productId, value }))
  );

  const getTableSatus = () => {
    axios
      .get(
        `${baseUrl}/core/api/v1/orders/get-running-order?tableId=${tableId}&shopId=${shopId}&hotel_id=${hotelId}&hotelId=${hotelId}`
      )
      .then((res) => {
        if (Object.entries(res.data).length > 0) {
          if (res.data.order_status === "RUNNING") {
            setOrderId(res.data.id);
            navigate(`/cat/${hotelId}/${shopId}/${tableId}/${tableNumber}`);
          }
        }
      })
      .catch((err) => console.error(err));
  };

  useEffect(() => {
    getTableSatus();
  }, []);

  const getAllShopCategory = () => {
    axios
      .get(
        `${baseUrl}/core/api/v1/product/category/get-all?shopId=${shopId}&hotel_id=${hotelId}&hotelId=${hotelId}`
      )
      .then((res) => {
        setCategories(
          res.data.filter((item) => item.product_category_status === "ACTIVE")
        );
        setSelectedCategory(res.data[0].id);
        setLoadingCategories(false); // Stop loading for categories
      })
      .catch((err) => console.error(err));
  };

  const getAllMenuItem = () => {
    axios
      .get(
        `${baseUrl}/core/api/v1/product/get-all?shopId=${shopId}&hotel_id=${hotelId}&hotelId=${hotelId}`
      )
      .then((res) => {
        setMenuItems(
          res.data.filter(
            (item) =>
              item.enable_on_guest_order === "YES" &&
              item.product_status === "ACTIVE" &&
              item.in_stock === "YES"
          )
        );
        setLoadingMenuItems(false); // Stop loading for menu items
      })
      .catch((err) => console.error(err));
  };
  const getMenuSelection = () => {
    axios
      .get(`${baseUrl}/web-api/table-qr-menu-only?hotelId=${hotelId}`)
      .then((res) => {
        setIsDisableMenu(res?.data);
      })
      .catch((err) => console.error(err));
  };

  useEffect(() => {
    getMenuSelection();
    getAllMenuItem();
    getAllShopCategory();
  }, []);

  useEffect(() => {
    if (menuItems.length > 0 && selectedCategory) {
      const filteredItems = menuItems.filter(
        (item) => item.product_category_id === selectedCategory
      );

      // Initialize quantities for selected menu items under the selected category
      const initialQuantities = { ...selectedMenuItems[selectedCategory] };

      filteredItems.forEach((item) => {
        if (!(item.id in initialQuantities)) {
          initialQuantities[item.id] = 0; // Default quantity to 0 if not set
        }
      });

      setSelectedMenuItems((prev) => ({
        ...prev,
        [selectedCategory]: initialQuantities,
      }));
    }
  }, [selectedCategory, menuItems]);

  // Function to handle quantity updates for the current category
  const handleQuantityChange = (itemId, action) => {
    setSelectedMenuItems((prev) => {
      const categoryQuantities = prev[selectedCategory] || {};
      const newQuantity =
        action === "add"
          ? (categoryQuantities[itemId] || 0) + 1
          : Math.max((categoryQuantities[itemId] || 0) - 1, 0);

      return {
        ...prev,
        [selectedCategory]: {
          ...categoryQuantities,
          [itemId]: newQuantity, // Update the quantity
        },
      };
    });
  };

  const addToCart = () => {
    navigate(`/cart/${hotelId}/${shopId}/${tableId}/${tableNumber}`);
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4 }}>
      <Typography variant="h5" gutterBottom>
        Add Items
      </Typography>

      <Box
        sx={{
          overflowX: "auto",
          whiteSpace: "nowrap",
          mb: 2,
          "&::-webkit-scrollbar": { display: "none" },
          "-ms-overflow-style": "none",
          "scrollbar-width": "none",
        }}
      >
        {loadingCategories ? (
          <Skeleton
            variant="rectangular"
            width={400}
            height={40}
            sx={{ mr: 1 }}
          />
        ) : (
          categories.map((category) => (
            <Button
              key={category.id}
              variant={`${
                selectedCategory === category.id ? "custom-button" : "outlined"
              }`}
              sx={{
                marginRight: 1,
              }}
              onClick={() => setSelectedCategory(category.id)}
            >
              {category.product_category_name}
            </Button>
          ))
        )}
      </Box>

      <Typography variant="h6" align="center" gutterBottom>
        Menu Items
      </Typography>
      <OutlinedInput
        onChange={(e) => setsearchTerm(e.target.value)}
        placeholder={"Search by name"}
        label={"Search by name"}
        className="mt-2 mb-4"
        notched={false}
        type="search"
        size="small"
        style={{
          borderRadius: "5px",
        }}
      />
      <List style={{ marginBottom: "3rem" }}>
        {loadingMenuItems
          ? Array.from(new Array(5)).map((_, index) => (
              <Card key={index} sx={{ mb: 2 }}>
                <CardContent style={{ padding: "0px" }}>
                  <ListItem sx={{ display: "flex", alignItems: "center" }}>
                    <Skeleton variant="rectangular" width={400} height={20} />
                  </ListItem>
                </CardContent>
              </Card>
            ))
          : menuItems
              .filter((item) => item.product_category_id === selectedCategory)
              .filter((val) => {
                if (searchTerm === "") {
                  return val;
                } else if (
                  val.product_name
                    .toLowerCase()
                    .includes(searchTerm.toLowerCase())
                ) {
                  return val;
                }
              })
              .map((item) => (
                <Card key={item.id} sx={{ mb: 2 }}>
                  <CardContent style={{ padding: "0px" }}>
                    <ListItem
                      key={item.id}
                      sx={{ display: "flex", alignItems: "center" }}
                    >
                      <div className="d-flex flex-column w-100">
                        <ListItemText primary={item.product_name} />
                        <ListItemText secondary={item.product_desc} />
                        <ListItemText
                          secondary={`Rs. ${parseFloat(
                            item.product_price
                          ).toFixed(2)}`}
                        />
                      </div>

                      {isDisableMenu || (
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <IconButton
                            onClick={() =>
                              handleQuantityChange(item.id, "remove")
                            }
                          >
                            <RemoveIcon />
                          </IconButton>
                          <TextField
                            value={
                              selectedMenuItems[selectedCategory]?.[item.id] ||
                              0
                            }
                            inputProps={{
                              readOnly: true,
                              style: { textAlign: "center" },
                            }}
                            sx={{ width: 50, mx: 1 }}
                            size="small"
                          />
                          <IconButton
                            onClick={() => handleQuantityChange(item.id, "add")}
                          >
                            <AddIcon />
                          </IconButton>
                        </Box>
                      )}
                    </ListItem>
                  </CardContent>
                </Card>
              ))}
      </List>

      {/* Fixed Proceed Button */}
      {isDisableMenu || (
        <Box
          sx={{
            position: "fixed",
            bottom: 0,
            left: 0,
            width: "100%",
            bgcolor: "background.paper",
            py: 2,
            borderTop: "1px solid #ddd",
            textAlign: "center",
          }}
        >
          <Button
            variant="custom-button"
            color="primary"
            onClick={addToCart}
            disabled={productsWithValues.length <= 0}
          >
            Proceed
          </Button>
        </Box>
      )}
    </Container>
  );
}

export default Category;
